import React, { useState, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useWebSocket, useChatSession } from './hooks';
import { MessageList, MessageInput, ConnectionStatus } from './components';
import './ChatBotPage.css';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
}

/**
 * Full-page ChatBot view
 * This can be used for the /chatbot route
 */
export const ChatBotPage: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { session, sessionId, loading: sessionLoading } = useChatSession();
  const { connect, disconnect, sendMessage, isConnected, connectionError } = useWebSocket(sessionId);
  const connectedSessionIdRef = useRef<string | null>(null);

  // When a new session starts, clear UI messages (no persistence across sessions/refresh).
  useEffect(() => {
    setMessages([]);
    setIsLoading(false);
  }, [sessionId]);

  // Connect when session is available - only once per session ID
  useEffect(() => {
    if (sessionId && !sessionLoading) {
      // Only connect if this is a new session ID
      if (connectedSessionIdRef.current !== sessionId) {
        console.log('[ChatBot] New session ID detected, connecting...', sessionId);
        connectedSessionIdRef.current = sessionId;
        connect(sessionId);
      }
    } else if (!sessionId) {
      // Reset when session is cleared
      connectedSessionIdRef.current = null;
    }

    // Cleanup: only disconnect on unmount, not on dependency changes
    return () => {
      // Don't disconnect here - let the WebSocket manage its own lifecycle
      // Only disconnect if component is actually unmounting (not just re-rendering)
    };
  }, [sessionId, sessionLoading]); // Removed connect from dependencies to prevent re-runs

  // Clear loading state on connection error
  useEffect(() => {
    if (connectionError) {
      setIsLoading(false);
    }
  }, [connectionError]);

  // Handle incoming messages from WebSocket
  useEffect(() => {
    const handleChatMessage = (event: Event) => {
      const customEvent = event as CustomEvent<{ type: string; sender?: string; message?: string; timestamp?: string }>;
      const data = customEvent.detail;
      console.log(data);
      if (data.type === 'message' && data.sender === 'bot' && data.message) {
        setIsLoading(false);
        const botMessage: Message = {
          id: Date.now().toString(),
          role: 'assistant',
          content: data.message,
          timestamp: new Date(data.timestamp || Date.now()),
        };
        setMessages((prev) => [...prev, botMessage]);
      }
    };

    const handleSystemMessage = (event: Event) => {
      const customEvent = event as CustomEvent<{ type: string; message?: string; content?: string; timestamp?: string }>;
      const data = customEvent.detail;
      if (data.type === 'system' || data.type === 'connection') {
        const systemMessage: Message = {
          id: Date.now().toString(),
          role: 'system',
          content: data.message || data.content || 'Connected',
          timestamp: new Date(data.timestamp || Date.now()),
        };
        setMessages((prev) => [...prev, systemMessage]);
      }
    };

    window.addEventListener('chat-message', handleChatMessage);
    window.addEventListener('system-message', handleSystemMessage);

    return () => {
      window.removeEventListener('chat-message', handleChatMessage);
      window.removeEventListener('system-message', handleSystemMessage);
    };
  }, []);

  const handleSendMessage = (content: string) => {
    if (!content.trim() || !isConnected || !sessionId) {
      console.warn('[ChatBot] Cannot send message:', { isConnected, sessionId });
      return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);
    sendMessage(content.trim());
  };

  if (sessionLoading && !sessionId) {
    return (
      <Box className="chatbot-page">
        <Box className="chatbot-page-content">
          <Box className="auth-prompt">
            <p>Creating chat session...</p>
          </Box>
        </Box>
      </Box>
    );
  }
  console.log({ messages });
  return (
    <Box className="chatbot-page">
      <Box className="chatbot-page-content">
        <Box className="chat-header">
          <ConnectionStatus isConnected={isConnected} error={connectionError} />
        </Box>
        <MessageList messages={messages} isLoading={isLoading} />
        <MessageInput onSendMessage={handleSendMessage} disabled={!isConnected || !sessionId} />
      </Box>
    </Box>
  );
};
